import { createIntl, createIntlCache } from "@formatjs/intl";
import Cookies from "js-cookie";
import { cloneElement, isValidElement } from "react";
import translationEn from "../locales/en.json";
import translationFr from "../locales/fr.json";

export type TranslationKey = keyof typeof translationFr;
type TranslationKeyEn = keyof typeof translationEn;
export type _MissingEnKeys = Exclude<TranslationKey, TranslationKeyEn>;

export type TranslationParams = Record<string, string | number>;
export type NestedTranslationParams = Record<
  string,
  string | number | ((children: React.ReactNode) => React.ReactNode)
>;

type I18nConfig = {
  cookieKey: string;
  cookieExpires: number;
  cookieDomain: string;
  locales: Record<string, typeof translationFr>;
};

const CONFIG = {
  // comes from website project bireme.io
  // we use the same cookie to sync the locale between the website and all apps
  cookieKey: "NEXT_LOCALE",
  cookieExpires: 365,
  cookieDomain: window.location.hostname.split(".").slice(-2).join("."),
  locales: {
    fr: translationFr,
    // if there there is type error, check _MissingEnKeys
    en: translationEn,
  },
} satisfies I18nConfig;

type Locale = keyof typeof CONFIG.locales;

const isLocale = (locale: string | undefined): locale is Locale =>
  Object.keys(CONFIG.locales).includes(locale as Locale);

const getLocale = (): Locale => {
  // get locale from cookie
  const cookieLocale = Cookies.get(CONFIG.cookieKey);
  if (isLocale(cookieLocale)) {
    return cookieLocale;
  }

  // if locale is not set in cookie, get it from browser
  const DEFAULT_LOCAL = "en";
  const browserLocale = navigator.language.split("-")[0];
  const locale = isLocale(browserLocale) ? browserLocale : DEFAULT_LOCAL;

  // set locale in cookie
  Cookies.set(CONFIG.cookieKey, locale, {
    expires: CONFIG.cookieExpires,
    domain: CONFIG.cookieDomain,
  });

  return locale;
};

export const setLocale = (locale: Locale) => {
  Cookies.set(CONFIG.cookieKey, locale, {
    expires: CONFIG.cookieExpires,
    domain: CONFIG.cookieDomain,
  });
  window.location.reload();
};

export const locale = getLocale();

const intl = createIntl(
  {
    defaultLocale: locale,
    fallbackOnEmptyString: false,
    locale: locale,
    messages: CONFIG.locales[locale],
  },
  createIntlCache(),
);

export const t = (key: TranslationKey, params?: TranslationParams) =>
  intl.formatMessage({ id: key, defaultMessage: translationFr[key] }, params).toString();

export const tNested = (key: TranslationKey, params?: NestedTranslationParams) => {
  const result = intl.formatMessage(
    { id: key, defaultMessage: translationFr[key] },
    // @ts-expect-error params can accept a function
    params,
  ) as string | (string | React.ReactElement)[];

  if (typeof result === "string") {
    return result;
  }

  return result.map((part, index) =>
    isValidElement(part) ? cloneElement(part, { key: index }) : part,
  );
};

type Currency = "EUR";

export const formatCurrency = (value: number, currency: Currency) =>
  new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
  }).format(value);

export const currencyLabels: Record<Currency, string> = {
  EUR: "€",
};
